import React, { FC, useEffect } from 'react';
import { AnimatedScreen } from 'Shared/components';
import { RouteComponentProps } from 'react-router-dom';
import styles from './styles.module.css';
import { useRewardsPoints } from 'Shared/hooks/useRewardsPoints';
import { Button } from '@clearcover/atlas';
import { RewardsPortalError } from '../RewardsPortalError';
import { useLazyRewardsApi, useNewRewardsApi } from 'Shared/services/Api';
import { reportEvent } from 'Shared/analytics';
import {
  events,
  hotels,
  sports,
  amazon,
  giftcard,
  home,
  prizeBox,
} from './images';
import {
  Category,
  GetAgentRedemptionResponse,
} from 'Rewards/services/endpoints/PostRedeem/types';
import { RefreshTimer } from './RefreshTimer';
import { Challenges } from './components/Challenges/Challenges';
import { ChallengesFailure } from './components/ChallengesFailure/ChallengesFailure';
import { env } from 'Shared/constants/env';
import { GetChallengesResponseType } from 'Rewards/services/endpoints/GetChallenges/types';

const SmallRewardCard: FC<{
  rewardText: string;
  altText: string;
  imageSrc: string;
  onClick: () => void;
}> = ({ rewardText, altText, imageSrc, onClick }) => {
  return (
    <div className={styles.smallRewardCard} onClick={onClick}>
      <div className={styles.smallRewardCardTitle}>
        <p className={styles.smallRewardText}>{rewardText}</p>
      </div>
      <img src={imageSrc} alt={altText} className={styles.image} />
    </div>
  );
};

const LargeRewardCard: FC<{
  rewardText: string;
  altText: string;
  imageSrc: string;
  onClick: () => void;
}> = ({ rewardText, altText, imageSrc, onClick }) => {
  return (
    <div className={styles.largeRewardCard} onClick={onClick}>
      <div className={styles.largeRewardCardContents}>
        <p className={styles.largeRewardText}>{rewardText}</p>
        <img src={imageSrc} alt={altText} className={styles.largeRewardImage} />
      </div>
    </div>
  );
};

const WelcomeBanner: FC<{}> = () => {
  return (
    <div className={styles.welcomeBanner}>
      <div className={styles.welcomeBannerTextContent}>
        <h2>Welcome to our Clearcover Rewards pilot program!</h2>
        <span className={styles.welcomeBannerText}>
          We’re offering Rewards to thank our partners for choosing Clearcover.
          Every point you earn is worth $1 to spend here at our Rewards Center.
          Start a quote today to see how much you can earn for each new policy
          sold!
        </span>
        <div className={styles.welcomeBannerButtonsContainer}>
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              reportEvent('rewards-center-welcome-banner:faq-button:click');
              window.open(
                'https://resources.clearcover.com/hc/en-us/articles/15343999859991',
                '_blank',
                'noreferrer'
              );

              // Prevents the button from still being focused when the user returns to the screen
              e.currentTarget.blur();
            }}
            data-testid="welcome-banner-faq-button"
          >
            Frequently Asked Questions
          </Button>
          <a
            className={styles.welcomeBannerLink}
            href="https://survey.survicate.com/34d6bb1af0e337be/?p=customerio&email=%7B%7Bcustomer.email%7D%7D"
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              reportEvent('rewards-center-welcome-banner:questions-link:click')
            }
            data-testid="welcome-banner-questions-link"
          >
            Questions? Contact Us
          </a>
          <a
            className={styles.welcomeBannerLink}
            href="https://clearcover.com/rewards-terms"
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              reportEvent(
                'rewards-center-welcome-banner:terms-and-conditions:click'
              )
            }
            data-testid="welcome-banner-tc-link"
          >
            Terms & Conditions
          </a>
        </div>
      </div>
      <img
        src={prizeBox}
        alt={'prize box'}
        className={styles.welcomeBannerImage}
      />
    </div>
  );
};

const TermsAndConditions: FC<{ onAgreeClick: () => void; error: boolean }> = ({
  onAgreeClick,
  error,
}) => {
  React.useEffect(() => {
    reportEvent('rewards-center-terms-and-conditions:display');
  }, []);
  React.useEffect(() => {
    if (error) {
      reportEvent('rewards-center-terms-and-conditions:error');
    }
  }, [error]);

  return (
    <div className={styles.termsAndConditions}>
      <h1>Welcome to Clearcover Rewards!</h1>
      <p className={styles.offeringRewardsText}>
        We’re offering Rewards to thank our partners for choosing Clearcover.
      </p>
      <p>Every point you earn is worth $1 to spend on things you love.</p>
      <div className={styles.termsAndConditionsDivider} />
      <span>
        To get started with Clearcover Rewards, please read and accept our Terms
        and Conditions
      </span>
      <Button
        variant="secondary"
        className={styles.viewTermsAndConditionsButton}
        onClick={(e) => {
          reportEvent(
            'rewards-center-terms-and-conditions:view-terms-and-conditions-button-press'
          );
          window.open(
            'https://clearcover.com/rewards-terms',
            '_blank',
            'noreferrer'
          );
          // Prevents the button from still being focused when the user returns to the screen
          e.currentTarget.blur();
        }}
        size="lg"
      >
        Click here to view Terms & Conditions
      </Button>
      <span>
        Once you have read and agreed to accept the terms, please click{' '}
        <span className={styles.agreeText}>I Agree</span> to continue to
        Clearcover Rewards!
      </span>
      <Button
        variant="primary"
        className={styles.agreeButton}
        onClick={() => {
          reportEvent(
            'rewards-center-terms-and-conditions:agree-to-terms-and-conditions-button-press'
          );
          onAgreeClick();
        }}
        size="lg"
      >
        I Agree
      </Button>
      {error && (
        <div className={styles.termsAndConditionsError}>
          Error processing request. Please try again.
        </div>
      )}
    </div>
  );
};

const RedeemPointsButton: FC<{
  onClick: () => void;
}> = ({ onClick }) => {
  useEffect(() => {
    reportEvent('rewards-center-redeem-points-button:display');
  }, []);

  return (
    <Button
      onClick={onClick}
      variant="primary"
      data-testid="redeem-points-button"
    >
      Redeem Points
    </Button>
  );
};

const ViewYourOrdersButton: FC<{
  onClick: () => void;
}> = ({ onClick }) => {
  useEffect(() => {
    reportEvent('rewards-center-view-your-orders-button:display');
  }, []);

  return (
    <Button
      onClick={onClick}
      variant="secondary"
      data-testid="view-your-orders-button"
    >
      View Your Orders
    </Button>
  );
};

export const RewardsPortal: FC<RouteComponentProps> = () => {
  useEffect(() => {
    reportEvent('rewards-center:display');
    const rewardsPortalVisitCount = localStorage.getItem(
      'rewardsPortalVisitCount'
    );
    let newRewardsPortalVisitCount =
      rewardsPortalVisitCount === null
        ? 1
        : Number(rewardsPortalVisitCount) + 1;
    localStorage.setItem(
      'rewardsPortalVisitCount',
      newRewardsPortalVisitCount.toString()
    );
    window._sva?.setVisitorTraits({
      showRewardsPortalSurvey: (newRewardsPortalVisitCount >= 3).toString(),
    });
  }, []);

  const {
    agentRewardsPointsLoading: agentRewardsLoading,
    agentRewardsPointsResponse: agentRewardsResponse,
    refreshAgentRewardsPoints: agentRewardsRefresh,
    agentRewardsPointsError: agentRewardsError,
  } = useRewardsPoints();

  const [{ loading: redemptionLoading }, agentRedemption] =
    useLazyRewardsApi<GetAgentRedemptionResponse>({
      method: 'POST',
      url: `/v1/redeem`,
    });

  const [
    {
      data: challengesResponse,
      loading: challengesLoading,
      refetch: challengesRefresh,
      error: challengesError,
    },
  ] = useNewRewardsApi<GetChallengesResponseType>(
    {
      url: `/v1/challenges`,
    },
    false,
    false
  );

  const rewardsCenterRefresh = () => {
    agentRewardsRefresh();
    env.rewardsCenterChallengesEnabled && challengesRefresh();
  };

  const [
    {
      loading: acceptTermsAndConditionLoading,
      error: acceptTermsAndConditionError,
    },
    acceptTermsAndConditions,
  ] = useLazyRewardsApi<GetAgentRedemptionResponse>({
    method: 'POST',
    url: `/v1/terms`,
  });

  const onClick = ({
    category,
    event,
  }: {
    category: Category;
    event: string;
  }) => {
    reportEvent(event);

    agentRedemption({
      data: { category },
    }).then(({ data: redemptionData }) => {
      window.open(redemptionData.redemptionUrl, '_blank', 'noreferrer');
    });
  };

  return (
    <>
      <AnimatedScreen
        loading={
          agentRewardsLoading ||
          acceptTermsAndConditionLoading ||
          (env.rewardsCenterChallengesEnabled ? challengesLoading : false)
        }
        testId="rewards-portal"
      >
        {agentRewardsError && (
          <RewardsPortalError rewardsRefresh={rewardsCenterRefresh} />
        )}
        {!agentRewardsError &&
          !agentRewardsResponse?.acceptedTermsAndConditions && (
            <TermsAndConditions
              onAgreeClick={() =>
                acceptTermsAndConditions().then(() => rewardsCenterRefresh())
              }
              error={!!acceptTermsAndConditionError}
            />
          )}
        {!agentRewardsError &&
          agentRewardsResponse?.acceptedTermsAndConditions && (
            <div
              className={redemptionLoading ? styles.loadingOverlay : undefined}
            >
              <div className={styles.title}>
                <h1>Clearcover Rewards</h1>
                <RefreshTimer refresh={rewardsCenterRefresh} />
              </div>
              <WelcomeBanner />
              <div className={styles.container}>
                <div className={styles.currentPointsPanel}>
                  <div>
                    <div className={styles.currentPointsHeading}>
                      current reward points
                    </div>
                    <div className={styles.currentPoints}>
                      {agentRewardsResponse?.currentPoints?.toLocaleString()}
                    </div>
                  </div>
                  <div className={styles.currentPointsPanelRight}>
                    <RedeemPointsButton
                      onClick={() =>
                        onClick({
                          category: null,
                          event: 'rewards-center-redeem-button-press',
                        })
                      }
                    />
                  </div>
                </div>
                <div className={styles.totalPointsPanel}>
                  <div>
                    <div className={styles.totalPointsHeading}>
                      total earned rewards
                    </div>
                    <div className={styles.totalPoints}>
                      {agentRewardsResponse?.lifetimePoints?.toLocaleString()}
                    </div>
                  </div>
                  <ViewYourOrdersButton
                    onClick={() => {
                      onClick({
                        category: 'ORDERS',
                        event: 'rewards-center-view-your-orders-button-press',
                      });
                    }}
                  />
                </div>
              </div>
              <div>
                {env.rewardsCenterChallengesEnabled && challengesResponse && (
                  <Challenges
                    challengesData={challengesResponse}
                    rewardsCenterRefresh={rewardsCenterRefresh}
                  />
                )}
                {env.rewardsCenterChallengesEnabled && challengesError && (
                  <ChallengesFailure
                    rewardsCenterRefresh={rewardsCenterRefresh}
                  />
                )}
              </div>
              <div className={styles.availableRewardsContainer}>
                <h2>Available Rewards</h2>
                <div className={styles.rewardCards}>
                  <SmallRewardCard
                    rewardText="Sports"
                    altText="Sports Rewards"
                    imageSrc={sports}
                    onClick={() =>
                      onClick({
                        category: 'SPORTS',
                        event: 'rewards-center-sports-button-press',
                      })
                    }
                  />

                  <SmallRewardCard
                    rewardText="Hotels"
                    altText="Hotel Rewards"
                    imageSrc={hotels}
                    onClick={() =>
                      onClick({
                        category: 'HOTELS',
                        event: 'rewards-center-hotels-button-press',
                      })
                    }
                  />

                  <SmallRewardCard
                    rewardText="Events"
                    altText="Events Rewards"
                    imageSrc={events}
                    onClick={() =>
                      onClick({
                        category: 'EVENTS',
                        event: 'rewards-center-events-button-press',
                      })
                    }
                  />

                  <SmallRewardCard
                    rewardText="Home"
                    altText="Home Rewards"
                    imageSrc={home}
                    onClick={() =>
                      onClick({
                        category: 'HOME',
                        event: 'rewards-center-home-button-press',
                      })
                    }
                  />
                </div>
                <div className={styles.rewardCards}>
                  <LargeRewardCard
                    rewardText="eGift Cards"
                    imageSrc={giftcard}
                    altText="eGift Card Rewards"
                    onClick={() =>
                      onClick({
                        category: 'EGIFT_CARDS',
                        event: 'rewards-center-egift-cards-button-press',
                      })
                    }
                  />
                  <LargeRewardCard
                    rewardText="Amazon"
                    imageSrc={amazon}
                    altText="Amazon Rewards"
                    onClick={() =>
                      onClick({
                        category: 'AMAZON',
                        event: 'rewards-center-amazon-button-press',
                      })
                    }
                  />
                </div>
              </div>
            </div>
          )}
      </AnimatedScreen>
    </>
  );
};
